import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, Loader2, Mail, Lock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { PoweredBy } from "@/components/Branding";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const dest = loc.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen bg-[#09090B] grid-texture flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="w-8 h-8 rounded-sm bg-blue-600 flex items-center justify-center"><Boxes className="w-5 h-5 text-white" /></div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight leading-none">ADAC Suite</div>
            <div className="font-mono text-[10px] text-zinc-500">As-constructed QA &amp; submission</div>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-[#101010] p-6">
          <div className="flex gap-1 mb-5 p-1 bg-[#0A0A0A] rounded-sm border border-zinc-800">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`tab-${m}`} onClick={() => setMode(m)}
                className={`flex-1 py-1.5 rounded-sm font-mono text-[11px] uppercase tracking-wider transition-colors duration-75 ${mode === m ? "bg-blue-600 text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
                {m === "login" ? "Sign in" : "Sign up"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <Field icon={User} testid="auth-name" placeholder="Name" value={name} onChange={setName} />
            )}
            <Field icon={Mail} testid="auth-email" placeholder="you@company.com" type="email" value={email} onChange={setEmail} />
            <Field icon={Lock} testid="auth-password" placeholder="Password" type="password" value={password} onChange={setPassword} />
            <Button type="submit" disabled={busy} data-testid="auth-submit" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="font-mono text-[10px] text-zinc-600">OR</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <Button onClick={google} variant="outline" data-testid="google-btn" className="w-full rounded-sm border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-900">
            <GoogleIcon /> Continue with Google
          </Button>
        </div>
        <p className="font-mono text-[10px] text-zinc-600 text-center mt-4">By continuing you agree to the <Link to="/terms" className="text-zinc-400 hover:text-zinc-200">Terms</Link> &amp; <Link to="/privacy" className="text-zinc-400 hover:text-zinc-200">Privacy Policy</Link>.</p>
        <div className="mt-3"><PoweredBy center /></div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
      <Input data-testid={testid} {...props} onChange={(e) => props.onChange(e.target.value)}
        className="h-10 pl-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs" required />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
  );
}
